import { ArrowRight, BarChart3, FileText, Loader2, Send, X } from 'lucide-react'
import { useState } from 'react'
import agentImage from '@/assets/images/agent.png'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/shared/components/ui/drawer'
import { Input } from '@/shared/components/ui/input'
import {
  aiCapabilities,
  quickActions,
  welcomeMessage,
} from '../data/aiCapabilities'
import type { AICapability } from '../types/ai-assistant.types'
import { AICapabilityCard } from './AICapabilityCard'
import { AIQuickActions } from './AIQuickActions'

interface AIAssistantDrawerProps {
  trigger: React.ReactNode
}

export function AIAssistantDrawer({ trigger }: AIAssistantDrawerProps) {
  const [open, setOpen] = useState(false)
  const [selectedCapability, setSelectedCapability] =
    useState<AICapability | null>(null)
  const [showConversation, setShowConversation] = useState(false)
  const [messages, setMessages] = useState<
    Array<{ role: 'user' | 'assistant'; content: string }>
  >([])
  const [inputValue, setInputValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const resetDrawer = () => {
    setSelectedCapability(null)
    setShowConversation(false)
    setMessages([])
    setInputValue('')
    setIsLoading(false)
  }

  const handleCapabilitySelect = (capability: AICapability) => {
    setSelectedCapability(capability)
  }

  const handleBackToMain = () => {
    if (showConversation) {
      setShowConversation(false)
    } else {
      setSelectedCapability(null)
    }
  }

  const handleStartConversation = (capability: AICapability) => {
    setShowConversation(true)
    // Initialize with a welcome message from the AI
    setMessages([
      {
        role: 'assistant',
        content: `Hi! I'm Cooboy, and I'm here to help you with ${capability.title.toLowerCase()}. What would you like to know or improve?`,
      },
    ])
  }

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return

    const userMessage = inputValue.trim()
    setInputValue('')
    setMessages(prev => [...prev, { role: 'user', content: userMessage }])
    setIsLoading(true)

    // Simulate AI response (replace with actual AI API call)
    setTimeout(() => {
      const aiResponse = generateAIResponse(userMessage, selectedCapability)
      setMessages(prev => [...prev, { role: 'assistant', content: aiResponse }])
      setIsLoading(false)
    }, 1000)
  }

  const generateAIResponse = (
    _userMessage: string,
    capability: AICapability | null
  ): string => {
    const responses = {
      revenue: [
        'I can help you identify revenue optimization opportunities. Let me analyze your billing patterns and usage data.',
        "Based on your query, I'd recommend looking at customers with high usage but low billing rates.",
        'Revenue leaks often occur in free tier overages and untracked premium features.',
      ],
      cost: [
        'I can help you analyze and reduce operational costs. Let me examine your AI provider spending.',
        'Cost optimization often starts with reviewing your most expensive API calls and models.',
        'I recommend switching to more cost-effective models for non-critical operations.',
      ],
      margin: [
        'Let me help you improve profit margins by analyzing customer profitability.',
        'Margin improvement typically involves optimizing both revenue and costs simultaneously.',
        'I can identify which customer segments are most and least profitable.',
      ],
      setup: [
        "I'll guide you through the setup process step by step.",
        "Let's start by configuring your signals and billing meters.",
        'I can help you establish best practices for tracking and monetization.',
      ],
      reports: [
        'I can generate comprehensive reports and analytics for you.',
        'What specific metrics or time period would you like me to focus on?',
        "I'll create visualizations that highlight your key performance indicators.",
      ],
    }

    const categoryResponses =
      responses[capability?.category as keyof typeof responses] ||
      responses['setup']
    return categoryResponses[
      Math.floor(Math.random() * categoryResponses.length)
    ]
  }

  const handleQuickAction = (actionId: string) => {
    const action = quickActions.find(a => a.id === actionId)
    if (action) {
      // TODO: Implement actual AI conversation/action
      // For now, show as selected capability
      const capability = aiCapabilities.find(
        cap => cap.category === action.category
      )
      if (capability) {
        setSelectedCapability(capability)
      }
    }
  }

  const renderWelcomeView = () => (
    <div className='h-full overflow-y-auto'>
      <div className='p-6 space-y-8'>
        {/* Welcome Header */}
        <div className='text-center space-y-4'>
          <div className='mx-auto w-16 h-16 rounded-full overflow-hidden bg-white border-2 border-blue-200'>
            <img
              src={agentImage}
              alt='Cooboy'
              className='w-full h-full object-contain'
            />
          </div>
          <div>
            <h2 className='text-2xl font-bold mb-2'>{welcomeMessage.title}</h2>
            <p className='text-muted-foreground text-lg leading-relaxed'>
              {welcomeMessage.subtitle}
            </p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className='space-y-4'>
          <h3 className='font-semibold text-lg'>Quick Actions</h3>
          <AIQuickActions
            actions={quickActions.slice(0, 6)}
            onActionClick={handleQuickAction}
          />
        </div>

        {/* Capabilities Grid */}
        <div className='space-y-4'>
          <h3 className='font-semibold text-lg'>What I can help you with</h3>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            {aiCapabilities.map(capability => (
              <AICapabilityCard
                key={capability.id}
                capability={capability}
                onClick={() => handleCapabilitySelect(capability)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )

  const renderCapabilityDetail = (capability: AICapability) => {
    return (
      <div className='h-full overflow-y-auto'>
        <div className='p-6 space-y-6'>
          {/* Header */}
          <div className='space-y-4'>
            <div className='space-y-2'>
              <div className='flex items-center gap-3'>
                <div>
                  <h2 className='text-xl font-bold'>{capability.title}</h2>
                  <Badge variant='outline' className='mt-1'>
                    {capability.category}
                  </Badge>
                </div>
              </div>
              <p className='text-muted-foreground text-lg'>
                {capability.description}
              </p>
            </div>
          </div>

          {/* Quick Actions for this capability */}
          {capability.quickActions.length > 0 && (
            <div className='space-y-4'>
              <h3 className='font-semibold'>Quick Actions</h3>
              <AIQuickActions
                actions={capability.quickActions}
                onActionClick={handleQuickAction}
              />
            </div>
          )}

          {/* Examples */}
          <div className='space-y-4'>
            <h3 className='font-semibold'>What I can help you with:</h3>
            <div className='space-y-3'>
              {capability.examples && capability.examples.length > 0 ? (
                capability.examples.map((example, index) => (
                  <div
                    // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                    key={index}
                    className='flex items-start gap-3 p-3 bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors cursor-pointer'
                  >
                    <ArrowRight className='h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0' />
                    <span className='text-sm text-gray-300'>{example}</span>
                  </div>
                ))
              ) : (
                <div className='p-3 bg-gray-900 rounded-lg'>
                  <span className='text-sm text-gray-400'>
                    No examples available
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Action Button */}
          <div className='pt-4 border-t'>
            <Button
              className='w-full'
              size='lg'
              onClick={() => handleStartConversation(capability)}
            >
              Start Conversation
            </Button>
          </div>
        </div>
      </div>
    )
  }

  const renderConversationView = () => (
    <div className='h-full flex flex-col'>
      {/* Messages */}
      <div className='flex-1 overflow-y-auto p-6 space-y-4'>
        {messages.map((message, index) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
            key={index}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] p-3 rounded-lg ${
                message.role === 'user'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-900'
              }`}
            >
              <p className='text-sm'>{message.content}</p>
            </div>
          </div>
        ))}
        {isLoading && (
          <div className='flex justify-start'>
            <div className='bg-gray-100 p-3 rounded-lg flex items-center gap-2'>
              <Loader2 className='h-4 w-4 animate-spin' />
              <span className='text-sm text-gray-600'>
                Cooboy is thinking...
              </span>
            </div>
          </div>
        )}
        {messages.length === 1 && (
          <div className='text-center py-8'>
            <div className='space-y-4'>
              <h3 className='font-semibold'>Tools:</h3>
              <div className='flex flex-wrap gap-2 justify-center'>
                <Badge variant='outline' className='gap-1'>
                  <BarChart3 className='h-3 w-3' />
                  Query data
                </Badge>
                <Badge variant='outline' className='gap-1'>
                  <FileText className='h-3 w-3' />
                  Search docs
                </Badge>
                <Badge variant='outline' className='gap-1'>
                  <ArrowRight className='h-3 w-3' />
                  Navigate
                </Badge>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className='border-t p-4'>
        <div className='flex gap-2'>
          <Input
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            placeholder='Ask away (/ for commands)'
            className='flex-1'
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                handleSendMessage()
              }
            }}
            disabled={isLoading}
          />
          <Button
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || isLoading}
            size='sm'
            className='px-3'
          >
            <Send className='h-4 w-4' />
          </Button>
        </div>
      </div>
    </div>
  )

  return (
    <Drawer
      open={open}
      onOpenChange={newOpen => {
        setOpen(newOpen)
        if (!newOpen) {
          resetDrawer()
        }
      }}
      direction='right'
    >
      <DrawerTrigger asChild>{trigger}</DrawerTrigger>
      <DrawerContent className='h-screen w-[60vw] min-w-[700px] max-w-[900px] ml-auto fixed inset-y-0 right-0 flex flex-col'>
        <DrawerHeader className='border-b flex-shrink-0 px-6 py-4'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-2'>
              {(selectedCapability || showConversation) && (
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={handleBackToMain}
                  className='p-1 h-auto mr-2'
                >
                  <ArrowRight className='h-4 w-4 rotate-180' />
                </Button>
              )}
              <div className='w-10 h-10 rounded-full  '>
                <img src={agentImage} alt='Cooboy' className='w-10' />
              </div>
              <div>
                <div className='flex items-center gap-2'>
                  <DrawerTitle>Cooboy</DrawerTitle>
                  <Badge variant='secondary' className='text-xs px-2 py-0.5'>
                    Beta
                  </Badge>
                </div>
                <DrawerDescription>
                  {showConversation
                    ? `${selectedCapability?.title} • Conversation with Cooboy`
                    : selectedCapability
                      ? `${selectedCapability.title} • Business Optimization Assistant`
                      : 'Your agent monetization specialist'}
                </DrawerDescription>
              </div>
            </div>
            <Button
              variant='ghost'
              size='sm'
              onClick={() => setOpen(false)}
              className='p-1 h-auto'
            >
              <X className='h-4 w-4' />
            </Button>
          </div>
        </DrawerHeader>

        <div className='flex-1 overflow-hidden'>
          {showConversation
            ? renderConversationView()
            : selectedCapability
              ? renderCapabilityDetail(selectedCapability)
              : renderWelcomeView()}
        </div>
      </DrawerContent>
    </Drawer>
  )
}
